import { useMemo } from 'react';
import { useAuth } from './useAuth';

const decodeJwtPayload = (token) => {
  if (!token) return {};
  const parts = token.split('.');
  if (parts.length !== 3) return {};
  try {
    const raw = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

export const extractAuthRoles = ({ token, user, roles = [] } = {}) => {
  const payload = decodeJwtPayload(token);
  const realmRoles = [
    ...(Array.isArray(roles) ? roles : []),
    ...(Array.isArray(user?.roles) ? user.roles : []),
    ...(Array.isArray(user?.realmRoles) ? user.realmRoles : []),
    ...(Array.isArray(payload?.roles) ? payload.roles : []),
    ...(Array.isArray(payload?.realm_access?.roles) ? payload.realm_access.roles : []),
    ...(Array.isArray(payload?.claims?.roles) ? payload.claims.roles : []),
  ].filter(Boolean);

  const clientRoles = {};
  for (const [clientId, access] of Object.entries(payload?.resource_access || {})) {
    clientRoles[clientId] = Array.isArray(access?.roles) ? access.roles : [];
  }
  for (const [clientId, values] of Object.entries(user?.clientRoles || {})) {
    clientRoles[clientId] = [...new Set([...(clientRoles[clientId] || []), ...(Array.isArray(values) ? values : [])])];
  }

  return {
    realmRoles: [...new Set(realmRoles)],
    clientRoles,
  };
};

export const makeRoleChecks = ({ realmRoles = [], clientRoles = {} } = {}) => {
  const realmSet = new Set(realmRoles);
  return {
    realmRoles,
    clientRoles,
    hasRealmRole: (role) => realmSet.has(role),
    hasAnyRealmRole: (wanted = []) => wanted.some((role) => realmSet.has(role)),
    hasClientRole: (clientId, role) => Boolean(clientRoles?.[clientId]?.includes(role)),
    hasAnyClientRole: (clientId, wanted = []) => wanted.some((role) => clientRoles?.[clientId]?.includes(role)),
  };
};

export const useAuthRoles = () => {
  const { user, roles } = useAuth();
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('authToken') : null;

  return useMemo(
    () => makeRoleChecks(extractAuthRoles({ token, user, roles })),
    [token, user, roles]
  );
};
